import * as Sentry from '@sentry/react';

const capturedErrors = new WeakSet<object>();

const isObjectError = (error: unknown): error is object =>
  (typeof error === 'object' && error !== null) || typeof error === 'function';

const alreadyCaptured = (error: unknown): boolean =>
  isObjectError(error) && capturedErrors.has(error);

const markCaptured = (error: unknown): void => {
  if (isObjectError(error)) capturedErrors.add(error);
};

export type StaleWriteDetectionStage = 'initial_guard' | 'retry_guard';

interface StaleWriteTelemetryParams {
  error: Error;
  schema: string;
  tableName: string;
  whereClause: Record<string, unknown>;
  changedFields: string[];
  guardedFields: string[];
  guardedTypes: Record<string, string | null>;
  detectionStage: StaleWriteDetectionStage;
}

export interface StaleWriteTelemetryContext {
  [key: string]: unknown;
  recordId?: string | number;
  changedFields: string[];
  guardedFields: string[];
  guardedTypes: Record<string, string | null>;
  detectionStage: StaleWriteDetectionStage;
}

export interface HandledOperationTelemetryContext {
  [key: string]: unknown;
  errorName?: string;
  operation?: string;
  tableName?: string;
  relevantField?: string;
  dbCol?: string;
  code?: string;
  status?: number;
  constraintName?: string;
  isConstraintError?: boolean;
  isStaleWrite?: boolean;
  staleFields?: string[];
}

const safeString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length <= 200 ? value : undefined;

const safeNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Keep only structural diagnostics; provider/SQL messages and details can contain PII. */
export const buildHandledOperationTelemetryContext = (
  error: unknown,
): HandledOperationTelemetryContext => {
  if (!isObjectError(error)) return {};
  const source = error as Record<string, unknown>;
  const constraintInfo = isObjectError(source.constraintInfo)
    ? source.constraintInfo as Record<string, unknown>
    : undefined;
  const staleFields = Array.isArray(source.staleFields)
    ? source.staleFields.filter((field): field is string => typeof field === 'string').sort()
    : undefined;

  return Object.fromEntries(Object.entries({
    errorName: safeString(source.name),
    operation: safeString(source.operation),
    tableName: safeString(source.tableName),
    relevantField: safeString(source.relevantField),
    dbCol: safeString(source.dbCol),
    code: safeString(source.code) ?? (
      isObjectError(source.originalError)
        ? safeString((source.originalError as Record<string, unknown>).code)
        : undefined
    ),
    status: safeNumber(source.status),
    constraintName: constraintInfo ? safeString(constraintInfo.constraint_name) : undefined,
    isConstraintError: typeof source.isConstraintError === 'boolean' ? source.isConstraintError : undefined,
    isStaleWrite: typeof source.isStaleWrite === 'boolean' ? source.isStaleWrite : undefined,
    staleFields,
  }).filter(([, value]) => value !== undefined)) as HandledOperationTelemetryContext;
};

export const captureHandledOperationError = (error: unknown): void => {
  if (alreadyCaptured(error)) return;
  markCaptured(error);
  const context = buildHandledOperationTelemetryContext(error);
  const safeError = new Error('Handled operation failed');
  safeError.name = 'HandledOperationError';

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error.kind', 'handled_operation_error');
    if (context.operation) scope.setTag('operation', String(context.operation));
    if (context.tableName) scope.setTag('db.table', String(context.tableName));
    if (context.code) scope.setTag('error.code', String(context.code));
    scope.setFingerprint([
      'handled-operation-error',
      String(context.operation ?? 'unknown'),
      String(context.tableName ?? 'unknown'),
      String(context.code ?? context.status ?? 'unknown'),
    ]);
    scope.setContext('operation_error', context);
    Sentry.captureException(safeError);
  });
};

/**
 * Build diagnostic context without including the compared field values. Those values can
 * contain customer data; field names, database types, and the base record ID are enough to
 * diagnose representation errors and identify the affected audit history.
 */
export const buildStaleWriteTelemetryContext = ({
  whereClause,
  changedFields,
  guardedFields,
  guardedTypes,
  detectionStage,
}: Omit<StaleWriteTelemetryParams, 'error' | 'schema' | 'tableName'>): StaleWriteTelemetryContext => {
  const rawRecordId = whereClause.id;
  const recordId = typeof rawRecordId === 'string' || typeof rawRecordId === 'number'
    ? rawRecordId
    : undefined;

  return {
    ...(recordId !== undefined ? { recordId } : {}),
    changedFields: [...changedFields].sort(),
    guardedFields: [...guardedFields].sort(),
    guardedTypes: Object.fromEntries(
      Object.entries(guardedTypes).sort(([left], [right]) => left.localeCompare(right)),
    ),
    detectionStage,
  };
};

export const captureStaleWriteConflict = (params: StaleWriteTelemetryParams): void => {
  if (alreadyCaptured(params.error)) return;
  markCaptured(params.error);
  const context = buildStaleWriteTelemetryContext(params);

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error.kind', 'stale_write_conflict');
    scope.setTag('db.schema', params.schema);
    scope.setTag('db.table', params.tableName);
    scope.setFingerprint([
      'stale-write-conflict',
      params.schema,
      params.tableName,
      ...context.changedFields,
    ]);
    scope.setContext('stale_write', context);
    Sentry.captureException(params.error);
  });
};
